/**
 * The authenticated user — server state, owned by the `['auth','me']` TanStack
 * Query, NOT Zustand. This module is the imperative face of that query cache
 * (reachable outside React: apiClient, contextRequiredHook, cross-tab) plus the
 * pure role/context derivations shared by the imperative code and the
 * `useCurrentUser` hook.
 *
 * Server data lives in one place (the query cache) so it self-refreshes on
 * `staleTime`/focus instead of going stale until the next imperative setUser —
 * the bug that motivated this migration.
 */
import type { User, UserContext, Organization } from '@/types';
import { queryClient } from '@/api/queryClient';
import { useStaffStore } from '@/store/useStaffStore';
import { getToken, setToken } from '@/auth/session';
import { clearSession } from '@/store/registry';
import { adoptSurvivingUser } from '@/store/userSwitch';
import { getEffectiveLevel, isNoCompanyLogin } from '@/lib/defaultRoute';

/** Query key for the current authenticated user (`GET /api/auth/me/`). */
export const AUTH_ME_KEY = ['auth', 'me'] as const;

// Re-export from api/auth so callers of AUTH_ME_KEY can get the canonical
// options from the same module. The function body (not top-level evaluation)
// references AUTH_ME_KEY, so the circular import is safe at runtime.
export { authMeQueryOptions } from '@/api/auth';

/** localStorage seed so a reload paints the last-known user instantly (then the
 *  query refetches in the background). Token is never included. */
const SEED_KEY = 'auth-me-seed';

export function readSeed(): User | null {
  try {
    const raw = localStorage.getItem(SEED_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeSeed(user: User | null): void {
  try {
    if (user) {
      const rest = { ...user };
      delete (rest as { token?: string }).token; // never persist the token
      localStorage.setItem(SEED_KEY, JSON.stringify(rest));
    } else {
      localStorage.removeItem(SEED_KEY);
    }
  } catch {
    // privacy mode — the seed is a nicety, not required
  }
}

// ============================================================
// Imperative accessors over the query cache
// ============================================================

export function getCurrentUser(): User | null {
  return queryClient.getQueryData<User>(AUTH_ME_KEY) ?? null;
}

/** Write the user into the cache (login, context switch, profile edit). */
export function setCurrentUser(user: User): void {
  queryClient.setQueryData<User>(AUTH_ME_KEY, user);
  writeSeed(user);
}

/**
 * Mark `['auth','me']` stale when `kind`/`id` is the user's CURRENT context, so
 * the mounted auth query refetches and context-derived settings (contact-name
 * format, org/network name) update immediately instead of after AUTH_STALE_TIME.
 * Editing some other org/network leaves the cache alone.
 */
export function invalidateAuthMeIfCurrentContext(kind: 'org' | 'network', id: number): void {
  const ctx = getCurrentUser()?.context;
  if (!ctx || ctx.type !== kind) return;
  const currentId = kind === 'org' ? ctx.organization?.id : ctx.network?.id;
  if (currentId !== id) return;
  invalidateAuthMe();
}

/**
 * Mark `['auth','me']` stale regardless of the current context. For writes whose
 * effect reaches contexts the caller cannot enumerate (a network setting that
 * every member org inherits, #761).
 */
export function invalidateAuthMe(): void {
  void queryClient.invalidateQueries({ queryKey: AUTH_ME_KEY });
}

// ============================================================
// User-switch detector (#1336)
// ============================================================

/**
 * The same token may start answering /me as a different user id (the person's
 * second login was merged into this one). The detector remembers the last
 * non-null id it saw on `['auth','me']` and calls `adoptSurvivingUser` once
 * when a value arrives with another id. It keeps its OWN last-seen id: it never
 * compares against the zustand store, whose mirror writes the other way.
 * `removed` (sign-out, `replaceSession`'s `clearSession`) resets it, so a fresh
 * sign-in is never a switch.
 */
let lastSeenUserId: number | null = null;

queryClient.getQueryCache().subscribe((event) => {
  const key = event?.query?.queryKey;
  if (!Array.isArray(key) || key[0] !== AUTH_ME_KEY[0] || key[1] !== AUTH_ME_KEY[1]) return;
  if (event.type === 'removed') {
    lastSeenUserId = null;
    return;
  }
  const data = event.query.state.data as User | undefined | null;
  const id = data?.id;
  if (typeof id !== 'number') return;
  const previous = lastSeenUserId;
  lastSeenUserId = id;
  if (previous !== null && previous !== id) adoptSurvivingUser(data as User, previous);
});

/**
 * Second tab (#1336): when another tab writes a seed whose user id differs from
 * this tab's cached user, re-read `/auth/me` now instead of at the next focus
 * after the 5-minute stale time. ONLY the refetch runs; the stored value is
 * never trusted as the user (a forged blob must not escalate, see
 * `installCrossTabDeauth`), the refetch's answer drives the switch detector.
 * Returns a disposer. Call once from main.tsx.
 */
export function installCrossTabUserSync(): () => void {
  const handler = (evt: StorageEvent) => {
    if (evt.key !== SEED_KEY || evt.newValue === null) return;
    let storedId: unknown;
    try {
      storedId = (JSON.parse(evt.newValue) as { id?: unknown } | null)?.id;
    } catch {
      return;
    }
    if (typeof storedId !== 'number') return;
    const cached = getCurrentUser();
    if (cached && cached.id !== storedId) invalidateAuthMe();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/** Remove the user (logout). */
export function clearCurrentUser(): void {
  // Clear the seed BEFORE removing the query: App's useAuthQuery observer
  // re-initializes via `initialData: () => readSeed()`, so a still-present seed
  // would immediately re-populate the "logged out" user in the cache.
  writeSeed(null);
  queryClient.removeQueries({ queryKey: AUTH_ME_KEY });
}

/**
 * Establish/refresh the session from a user payload (login, SSO, profile
 * edit, context settings, merge-refresh). /auth/me responses carry no token, so
 * inherit the live session token rather than overwrite it with undefined.
 * Replaces the old `useAuthStore.setUser`.
 */
export function signIn(user: User): void {
  const token = user.token ?? getToken();
  if (token) setToken(token);
  setCurrentUser((token !== undefined ? { ...user, token } : user) as User);
}

/**
 * A FRESH credential takes over this device's session (vingreact#721, class
 * of #719): a password sign-in, a code verification that mints a token,
 * an `/sso` arrival whose exchange succeeded. Every one of those goes through
 * here; `src/__tests__/signInReplacesSession.guard.test.ts` enforces it.
 *
 * Why the clear comes first: the only cross-tab signal is the token REMOVAL
 * (`installCrossTabDeauth` reacts to `newValue === null`; a browser never
 * fires `storage` for a same-value write, and a new value is deliberately not
 * trusted). `clearSession()` removes the token, which makes every other open
 * tab drop the previous session's cached data exactly once, and wipes this
 * tab's query cache and stores; `signIn` then writes the new token, which the
 * other tabs pick up from storage on their next request. A bare `signIn` with
 * a new token would leave those tabs showing the previous person's data and
 * sending the new token under it.
 *
 * A plain `signIn` is only for REFRESHING the session already in storage
 * (an `/auth/me` re-read, a stored-session validation, a profile or context
 * update) — never for a new token.
 */
export function replaceSession(user: User & { token: string }): void {
  clearSession();
  signIn(user);
}

/**
 * Merge a partial update into the cached user. Ports the two behaviors the old
 * Zustand `updateUser` guarded:
 *  - M4: when a new `context` arrives without an explicit `organization`, derive
 *    the deprecated top-level `organization` from it (or clear it for
 *    network/null contexts) so nothing reads a stale org identity.
 *  - F4: when `isStaff` flips false, clear any staff org impersonation.
 */
export function updateCurrentUser(updates: Partial<User>): void {
  queryClient.setQueryData<User>(AUTH_ME_KEY, (prev) => {
    if (!prev) return prev;
    const next = { ...prev, ...updates } as User;
    if (updates.context !== undefined && updates.organization === undefined) {
      const nextCtx = updates.context;
      if (nextCtx && nextCtx.type === 'org' && nextCtx.organization) {
        next.organization = {
          id: nextCtx.organization.id,
          name: nextCtx.organization.name ?? '',
          identifier: nextCtx.organization.identifier,
          access: nextCtx.organization.access,
        } as Organization;
      } else {
        next.organization = undefined as unknown as Organization;
      }
    }
    writeSeed(next);
    return next;
  });
  if (updates.isStaff === false) {
    useStaffStore.setState({ orgOverride: null });
  }
}

// ============================================================
// Pure role / context derivations (were Zustand getters)
// ============================================================

// Role checks all go through getEffectiveLevel (src/lib/defaultRoute.ts): in a
// network context the level is the network seat's role (#1322), else the org
// context level, else the top-level level.
export function isAdmin(user: User | null): boolean {
  return user ? getEffectiveLevel(user) === 'admin' : false;
}
export function isManager(user: User | null): boolean {
  return user ? getEffectiveLevel(user) === 'manager' : false;
}
export function isViewer(user: User | null): boolean {
  return user ? getEffectiveLevel(user) === 'viewer' : false;
}
export function isStaff(user: User | null): boolean {
  return user?.isStaff ?? false;
}
export function isSuperuser(user: User | null): boolean {
  return user?.isSuperuser ?? false;
}
export function canSwitchContext(user: User | null): boolean {
  return user?.canSwitchContext ?? false;
}
/**
 * True when the user has an org/network context selected.
 *
 * The wire shape for "no context" is NOT a missing field: `/auth/me` answers a
 * no-context user with `context: { type: null, organization: null, network:
 * null }` (vingapi `build_user_context`). Gating on object truthiness let
 * `GET /api/config/` fire and 400 for every membership-less user (vingreact#64,
 * prod condition 428). Every "has a context" decision goes through this
 * predicate; never test `user.context` itself.
 */
export function hasSelectedContext(user: User | null): boolean {
  return user?.context?.type != null;
}
/** The selected context, or null — including the wire's `{ type: null }` shape. */
export function currentContext(user: User | null): UserContext | null {
  return hasSelectedContext(user) ? (user?.context ?? null) : null;
}
/** True when the user can switch context but hasn't selected one yet. */
export function requiresContextSelection(user: User | null): boolean {
  if (!user) return false;
  if (!user.canSwitchContext) return false;
  if (user.context?.type) return false;
  // #1400: no active membership, nothing to choose; the login lands on the viewer dashboard.
  if (isNoCompanyLogin(user)) return false;
  return true;
}
